/**
 * 来場者向け 緊急/重要なお知らせ強制表示モーダル
 * ホーム画面表示のたびに未既読の緊急お知らせがあれば表示し、❌ボタン押下まで閉じられない
 */

import React, { useState } from 'react';
import { Image, Modal, Pressable, ScrollView, Text, View } from '../../../shared/components/WebPrimitives';

/**
 * 来場者向け緊急/重要なお知らせモーダル
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示可否
 * @param {{ noticeId: string, title: string, body: string, imageUrl: string } | null} props.notice - 表示対象お知らせ
 * @param {(noticeId: string) => Promise<void>} props.onAcknowledge - ❌押下時の既読登録処理
 * @returns {JSX.Element} 緊急お知らせモーダル
 */
const EmergencyNoticeModal = ({ visible, notice, onAcknowledge }) => {
  /** 既読処理中状態 */
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  /**
   * ❌押下時の既読登録を実行する
   * @returns {Promise<void>}
   */
  const handlePressClose = async () => {
    if (!notice?.noticeId || isAcknowledging) {
      return;
    }

    setIsAcknowledging(true);

    try {
      await onAcknowledge(notice.noticeId);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <Modal
      accessibilityLabel={`緊急のお知らせ: ${notice?.title ?? ''}`}
      animationType="fade"
      dismissible={false}
      onRequestClose={() => {}}
      statusBarTranslucent
      transparent
      visible={Boolean(visible && notice)}
    >
      <View className="flex-1 items-center justify-center bg-black/80 px-4 py-6">
        <View className="w-full max-w-[480px] rounded-none border-4 border-app-quest-night bg-[#FFF4C2] shadow-[6px_6px_0_rgba(22,36,58,0.34)]">
          <View className="flex-row items-center justify-between border-b-4 border-app-quest-night bg-app-danger px-4 py-3">
            <Text className="text-base font-extrabold tracking-wide text-white">🚨 緊急のお知らせ</Text>
            <Pressable
              accessibilityLabel="緊急のお知らせを閉じる"
              accessibilityRole="button"
              className="h-8 w-8 items-center justify-center rounded-none border-2 border-white bg-[#7B1A1A]"
              disabled={isAcknowledging}
              onPress={handlePressClose}
              testID="emergency-notice-close-button"
            >
              <Text className="text-base font-extrabold text-white">✕</Text>
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 16 }} style={{ maxHeight: 480 }}>
            <Text className="text-lg font-extrabold text-app-text">{notice?.title ?? ''}</Text>

            {notice?.imageUrl ? (
              <Image
                accessibilityLabel="お知らせ添付画像"
                className="mt-3 h-48 w-full rounded-none border-2 border-app-quest-night"
                decoding="async"
                height={192}
                resizeMode="cover"
                source={{ uri: notice.imageUrl }}
              />
            ) : null}

            <Text className="mt-3 text-sm leading-6 text-app-text">{notice?.body ?? ''}</Text>
          </ScrollView>

          <View className="border-t-2 border-app-quest-night p-3">
            <Text className="mb-2 text-xs leading-5 text-app-text-secondary">
              安全に関わる重要なお知らせのため、内容を確認して「閉じる（確認しました）」を押してください。Escapeや背景操作では閉じません。
            </Text>
            <Pressable
              accessibilityRole="button"
              className="items-center justify-center rounded-none border-2 border-app-quest-night bg-app-quest-gold px-4 py-3"
              disabled={isAcknowledging}
              onPress={handlePressClose}
            >
              <Text className="text-sm font-bold text-app-quest-night">
                {isAcknowledging ? '処理中...' : '✕ 閉じる（確認しました）'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default EmergencyNoticeModal;
